import { useCallback } from 'react';
import { snapTo15Min, toDateKey } from '../../../../../utils/dateUtils';

/**
 * Hook for Timeline-specific interaction calculations.
 */
export function useTimelineInteractions({
    scrollRef,
    actualAlldayWidth,
    dynamicHourWidth,
    hourStart,
    GRID_BUFFER,
    assignments,
    setInteraction
}) {

    const calculateInteractionState = useCallback((e, currentInteraction) => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer || !currentInteraction) return null;
        const containerRect = scrollContainer.getBoundingClientRect();
        
        if (e.clientX < containerRect.left || e.clientX > containerRect.right) return null;

        const mouseXInContainer = e.clientX - containerRect.left;
        const stickyWidth = 80 + actualAlldayWidth;
        const isOverAllDay = mouseXInContainer < stickyWidth;

        const elements = document.elementsFromPoint(e.clientX, e.clientY);
        let row = elements.find(el => el.classList.contains('timeline-day-row'));
        if (!row) {
            const rows = Array.from(document.querySelectorAll('.timeline-day-row'));
            if (rows.length > 0) {
                 row = rows.reduce((prev, curr) => {
                     const pRect = prev.getBoundingClientRect();
                     const cRect = curr.getBoundingClientRect();
                     return Math.abs(cRect.top + (cRect.height/2) - e.clientY) < Math.abs(pRect.top + (pRect.height/2) - e.clientY) ? curr : prev;
                 });
            }
        }
        if (!row) return null;

        const hoverDayStr = row.getAttribute('data-day');
        const activeDay = hoverDayStr ? new Date(hoverDayStr) : (currentInteraction.currentDay || new Date(currentInteraction.origS));
        activeDay.setHours(0,0,0,0);

        const gridX = (e.clientX - containerRect.left - stickyWidth) + scrollContainer.scrollLeft - GRID_BUFFER;
        const hoursFromLeft = Math.max(0, gridX / dynamicHourWidth);
        
        const mouseTime = new Date(activeDay);
        const totalMinutes = Math.round((hourStart + hoursFromLeft) * 60);
        mouseTime.setHours(0, totalMinutes, 0, 0);
        const snappedMouseTime = snapTo15Min(mouseTime);

        let finalS = new Date(currentInteraction.origS);
        let finalE = new Date(currentInteraction.origE);
        let visualTopOffset = currentInteraction.visualTopOffset || 0;
        let targetLane = currentInteraction.targetLane || 0;

        if (currentInteraction.type === 'move' || currentInteraction.type === 'move-allday') {
            const deltaX = e.clientX - currentInteraction.startX;
            const deltaY = e.clientY - currentInteraction.startY;
            
            if (isOverAllDay) {
                finalS = new Date(activeDay);
                finalS.setHours(0,0,0,0);
                const durationMs = new Date(currentInteraction.origE) - new Date(currentInteraction.origS);
                finalE = new Date(finalS.getTime() + Math.max(0, durationMs));
                finalS.setHours(0,0,0,0);
                finalE.setHours(0,0,0,0);
                if (finalE < finalS) finalE = new Date(finalS);
            } else {
                if (currentInteraction.type === 'move-allday') {
                    finalS = snappedMouseTime;
                    finalE = new Date(finalS.getTime() + 60 * 60 * 1000);
                } else {
                    const durationMs = new Date(currentInteraction.origE) - new Date(currentInteraction.origS);
                    const origStart = new Date(currentInteraction.origS);
                    const deltaMinutes = (deltaX / dynamicHourWidth) * 60;
                    const currentSForCalc = new Date(activeDay);
                    currentSForCalc.setHours(origStart.getHours(), origStart.getMinutes() + deltaMinutes, 0, 0);
                    finalS = snapTo15Min(currentSForCalc);
                    finalE = new Date(finalS.getTime() + durationMs);
                }
            }

            const rowRect = row.getBoundingClientRect();
            const SENSE_MARGIN = 7;
            const rowInnerHeight = rowRect.height - (2 * SENSE_MARGIN);
            const overlapped = assignments.filter(a => {
                if (String(a.id) === String(currentInteraction.assignment.id)) return false;
                const aS = new Date(a.start_datetime);
                const aE = new Date(a.end_datetime);
                const aStartDay = toDateKey(aS);
                const aEndDay = toDateKey(aE);
                return hoverDayStr >= aStartDay && hoverDayStr <= aEndDay && finalS < aE && finalE > aS;
            });
            const clusterSize = overlapped.length + 1;
            const visualLaneHeight = rowInnerHeight / clusterSize;
            const relativeY = e.clientY - rowRect.top - SENSE_MARGIN;
            targetLane = Math.max(0, Math.min(clusterSize - 1, Math.floor(relativeY / visualLaneHeight)));
            const idealTopRelative = (e.clientY - rowRect.top) - (currentInteraction.offsetWithinTileY || 0);
            const snappedTopRelative = SENSE_MARGIN + (targetLane * visualLaneHeight);
            visualTopOffset = idealTopRelative - snappedTopRelative;

            return { finalS, finalE, activeDay, visualTopOffset, targetLane, isOverAllDay, moved: Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10 };
        } else if (currentInteraction.type === 'resize-left') {
            const limitMinutes = new Date(currentInteraction.origE).getTime() - (15 * 60000);
            finalS = snappedMouseTime.getTime() > limitMinutes ? new Date(limitMinutes) : snappedMouseTime;
            return { finalS, finalE, activeDay, visualTopOffset, targetLane, moved: true };
        } else if (currentInteraction.type === 'resize-right') {
            const limitMinutes = new Date(currentInteraction.origS).getTime() + (15 * 60000);
            finalE = snappedMouseTime.getTime() < limitMinutes ? new Date(limitMinutes) : snappedMouseTime;
            return { finalS, finalE, activeDay, visualTopOffset, targetLane, moved: true };
        } else if (currentInteraction.type === 'resize-top') {
            const limitDay = new Date(currentInteraction.origE);
            limitDay.setHours(0,0,0,0);
            finalS = activeDay.getTime() >= limitDay.getTime() ? new Date(limitDay.getTime()) : activeDay;
            finalS.setHours(0,0,0,0);
            return { finalS, finalE, activeDay, visualTopOffset, targetLane, moved: true };
        } else if (currentInteraction.type === 'resize-bottom') {
            const limitDay = new Date(currentInteraction.origS);
            limitDay.setHours(0,0,0,0);
            finalE = activeDay.getTime() <= limitDay.getTime() ? new Date(limitDay.getTime()) : activeDay;
            finalE.setHours(0,0,0,0);
            return { finalS, finalE, activeDay, visualTopOffset, targetLane, moved: true };
        }
        return null;
    }, [actualAlldayWidth, assignments, dynamicHourWidth, hourStart, scrollRef, GRID_BUFFER]);

    const handleInteractionStart = useCallback((e, type, assignment, col) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        if (!['move', 'move-allday'].includes(type)) e.preventDefault();
        
        const tileRect = e.currentTarget.getBoundingClientRect();
        const offsetWithinTileY = e.clientY - tileRect.top;

        setInteraction({
            type,
            assignment,
            startY: e.clientY,
            startX: e.clientX,
            currentX: e.clientX,
            currentY: e.clientY,
            offsetWithinTileY,
            origS: assignment.start_datetime,
            origE: assignment.end_datetime,
            origCol: col, 
            currentS: new Date(assignment.start_datetime),
            currentE: new Date(assignment.end_datetime),
            currentDay: new Date(assignment.start_datetime),
            visualTopOffset: 0,
            targetLane: col || 0,
            isOverAllDay: ['move-allday', 'resize-top', 'resize-bottom'].includes(type),
            moved: false,
            isOverSidebar: false
        });
    }, [setInteraction]);

    return {
        calculateInteractionState,
        handleInteractionStart
    };
}
